import React from 'react';
import { NavLink } from 'react-router-dom';
import { SEO } from '../components/SEO';
import { COMPANY_INFO, SITE_IMAGES, getCdnImage } from '../data/content';

export function Fordele() {
  return (
    <>
      <SEO
        title="Fordele ved SYSTEM 3E"
        description="Oplev alle fordelene ved SYSTEM 3E: Op til 3-4 gange hurtigere opbygning, 100% tør Click-Click montage, uforgængeligt perlit og optimal varmeisolering."
      />

      {/* Hero */}
      <section className="bg-[#243447] text-white py-16 sm:py-24 relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={getCdnImage(SITE_IMAGES.hero, 1600, 80)}
            alt="Fordele ved SYSTEM 3E"
            className="w-full h-full object-cover opacity-45"
          />
          <div className="absolute inset-0 bg-[#162332]/45" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-[#819FA8] font-extrabold text-xs uppercase tracking-widest block mb-2">
              Samlet Overblik
            </span>
            <h1 className="text-3xl sm:text-5xl font-black text-white tracking-tight mb-6">
              Hvorfor ændrer SYSTEM 3E måden, vi bygger på?
            </h1>
            <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
              Teknologisk præcision, hurtigere udførelse og maksimal energieffektivitet i én samlet dansk forhandlerløsning.
            </p>
          </div>
        </div>
      </section>

      {/* Big Statement Section */}
      <section className="py-16 sm:py-20 bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-xs font-extrabold text-[#122a4e] uppercase tracking-widest mb-3">
            Hurtighed & Effektivitet
          </p>
          <h2 className="text-2xl sm:text-4xl font-black text-slate-900 leading-tight mb-6">
            "Råhuset til et hus på omkring 110 m² kan opføres på 8 timer under optimalt tilrettelagte byggepladsforhold."
          </h2>
          <p className="text-slate-600 text-sm sm:text-base max-w-2xl mx-auto">
            SYSTEM 3E fjerner tørretider, reducerer tunge løft og minimerer risikoen for indbygget fugt i muren.
          </p>
        </div>
      </section>

      {/* Key Numbers / Fact Grid */}
      <section className="py-12 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 text-center">
            <div className="p-6 bg-slate-800/80 rounded-2xl border border-slate-700">
              <span className="text-3xl sm:text-5xl font-black text-[#446ba9] block mb-2">0,198</span>
              <span className="text-slate-300 font-bold text-xs uppercase block mb-1">U-Værdi (W/m²K)</span>
              <span className="text-slate-400 text-[11px]">Sublim isoleringsevne</span>
            </div>
            <div className="p-6 bg-slate-800/80 rounded-2xl border border-slate-700">
              <span className="text-3xl sm:text-5xl font-black text-white block mb-2">REI 240</span>
              <span className="text-slate-300 font-bold text-xs uppercase block mb-1">Brandmodstand</span>
              <span className="text-slate-400 text-[11px]">Op til 240 minutter + M</span>
            </div>
            <div className="p-6 bg-slate-800/80 rounded-2xl border border-slate-700">
              <span className="text-3xl sm:text-5xl font-black text-[#446ba9] block mb-2">0%</span>
              <span className="text-slate-300 font-bold text-xs uppercase block mb-1">Våd Mørtel/Lim</span>
              <span className="text-slate-400 text-[11px]">100% tør samling</span>
            </div>
            <div className="p-6 bg-slate-800/80 rounded-2xl border border-slate-700">
              <span className="text-3xl sm:text-5xl font-black text-white block mb-2">100%</span>
              <span className="text-slate-300 font-bold text-xs uppercase block mb-1">Dansk Eneforhandler</span>
              <span className="text-slate-400 text-[11px]">{COMPANY_INFO.name}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Split Feature 1: Click-Click vs. Traditional */}
      <section className="py-16 sm:py-24 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#122a4e] font-bold text-xs uppercase tracking-widest block mb-2">
                Teknologisk Sammenligning
              </span>
              <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-4">
                Slut med mørtelsvineri, tørretider og svindrevner
              </h2>
              <p className="text-slate-700 text-sm leading-relaxed mb-6">
                I traditionelt murværk udgør mørtelfugen ofte et svagt punkt, der kræver tørretid, rigtige temperaturer og stor faglig præcision. Med SYSTEM 3E klikker de formfaste elementer sammen som et mekanisk urværk.
              </p>

              <div className="space-y-3">
                <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-emerald-600 text-lg mt-0.5" aria-hidden="true" />
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">Hurtig fremdrift uanset vejret</h4>
                    <p className="text-slate-600 text-xs">Kan opføres i frost, regn eller varme, da der ikke bruges vand i samlingerne.</p>
                  </div>
                </div>
                <div className="bg-white p-4 rounded-xl border border-slate-200 flex items-start gap-3">
                  <i className="fa-solid fa-circle-check text-emerald-600 text-lg mt-0.5" aria-hidden="true" />
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">Ingen risiko for skimmel eller indbygget fugt</h4>
                    <p className="text-slate-600 text-xs">Tørre elementer sikrer et sundt indeklima helt fra den dag, huset tages i brug.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="rounded-2xl overflow-hidden bg-transparent">
              <img
                src={getCdnImage(SITE_IMAGES.clickClickDemo, 1000, 85)}
                alt="Click Click Samling Detail"
                className="w-full h-[380px] object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Split Feature 2: Perlite Insulation & Sustainability */}
      <section className="py-16 sm:py-24 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center lg:flex-row-reverse">
            <div className="lg:order-2">
              <span className="text-[#122a4e] font-bold text-xs uppercase tracking-widest block mb-2">
                Materiale & Miljø
              </span>
              <h2 className="text-3xl font-extrabold text-slate-900 tracking-tight mb-4">
                Naturligt perlit – uforgængeligt, isolerende & brandsikkert
              </h2>
              <p className="text-slate-700 text-sm leading-relaxed mb-6">
                Perlit er et naturligt forekommende mineral skabt af vulkansk aktivitet. Når det opvarmes, udvider det sig og danner en letvægtsstruktur med milliarder af mikroskopiske luftlommer.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-slate-900 text-sm mb-1">Mindre Affald</h4>
                  <p className="text-slate-600 text-xs">Projektets elementer beregnes præcist inden levering, hvilket minimerer tilskæringsspild på pladsen.</p>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-200">
                  <h4 className="font-bold text-slate-900 text-sm mb-1">Total Brandsikkerhed</h4>
                  <p className="text-slate-600 text-xs">Perlit brænder ikke og afgiver hverken røg eller giftige dampe ved ekstrem varme.</p>
                </div>
              </div>
            </div>

            <div className="lg:order-1 rounded-2xl overflow-hidden bg-transparent">
              <img
                src={getCdnImage("https://assets.auxodev.dk/assets/6db9bc06-2d1f-414d-89eb-eaf682030584/imgi-2-s1-wp-2.webp", 1000, 85)}
                alt="Perlite Materiale Close-up"
                className="w-full h-[380px] object-contain"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Concise Advantages Checklist */}
      <section className="py-16 sm:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">
              Oversigt over nøglefordelene
            </h2>
            <p className="text-slate-600 text-sm mt-2">
              For både bygherrer, arkitekter og udførende håndværkere.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <i className="fa-solid fa-stopwatch text-[#122a4e] text-2xl mb-3" aria-hidden="true" />
              <h3 className="font-bold text-slate-900 text-base mb-2">Markant hurtigere montage</h3>
              <p className="text-slate-600 text-xs leading-relaxed">
                Op til 3-4 gange hurtigere fremdrift sammenlignet med traditionelle murstens- eller gasbetonløsninger.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <i className="fa-solid fa-puzzle-piece text-[#122a4e] text-2xl mb-3" aria-hidden="true" />
              <h3 className="font-bold text-slate-900 text-base mb-2">Præcis modulteknik</h3>
              <p className="text-slate-600 text-xs leading-relaxed">
                Elementerne fremstilles med snævre tolerancer for 100% vinkelrette og snorlige vægge.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <i className="fa-solid fa-cloud-sun text-[#122a4e] text-2xl mb-3" aria-hidden="true" />
              <h3 className="font-bold text-slate-900 text-base mb-2">Uafhængig af vejrlig</h3>
              <p className="text-slate-600 text-xs leading-relaxed">
                Montering kan finde sted hele året rundt, også i vinterhalvåret, da ingen våde mørtler fryser.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <i className="fa-solid fa-shield-halved text-[#122a4e] text-2xl mb-3" aria-hidden="true" />
              <h3 className="font-bold text-slate-900 text-base mb-2">Ekspertdokumenteret kvalitet</h3>
              <p className="text-slate-600 text-xs leading-relaxed">
                Gennemtestet U-værdi på 0,198 W/m²K og brandmodstandsklasse REI 240 + M.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <i className="fa-solid fa-hand-holding-hand text-[#122a4e] text-2xl mb-3" aria-hidden="true" />
              <h3 className="font-bold text-slate-900 text-base mb-2">Ergonomisk arbejdsmiljø</h3>
              <p className="text-slate-600 text-xs leading-relaxed">
                Håndværkerne undgår tunge løfteborde, våde mørtelbøtter og slidsomme tunge løft på byggepladsen.
              </p>
            </div>

            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm">
              <i className="fa-solid fa-award text-[#122a4e] text-2xl mb-3" aria-hidden="true" />
              <h3 className="font-bold text-slate-900 text-base mb-2">Dansk Eneforhandling</h3>
              <p className="text-slate-600 text-xs leading-relaxed">
                AGT Nordic Group ApS garanterer sikker levering, lokal rådgivning og dansk support.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-4xl font-extrabold mb-4">
            Vil du udnytte fordelene ved SYSTEM 3E?
          </h2>
          <p className="text-slate-300 text-sm sm:text-base max-w-xl mx-auto mb-8">
            Lad os hjælpe med at gennemgå dit næste byggeprojekt og beregne fordelene for dig.
          </p>
          <NavLink
            to="/kontakt"
            className="bg-[#122a4e] hover:bg-[#1d3b66] text-white font-bold px-8 py-4 rounded-xl text-base transition-all inline-block shadow-lg"
          >
            Kontakt AGT Nordic Group ApS
          </NavLink>
        </div>
      </section>
    </>
  );
}
